import { mkdir, readFile, writeFile } from "node:fs/promises"
import { join } from "node:path"
import {
  SKILL_BODY,
  SKILL_DESCRIPTION,
  SKILL_GLOBS,
  SKILL_NAME,
} from "./content.js"

/**
 * How a file is written when something is already there.
 *
 * - `own`   the file belongs to us, so a rewrite is a plain overwrite guarded
 *           by `--force`.
 * - `share` the file belongs to the project and may hold unrelated
 *           instructions, so we only ever replace our own marked block.
 */
type Mode = "own" | "share"

export interface SkillTarget {
  /** Value accepted by `--ide`. */
  id: string
  /** Editor name, for CLI output. */
  label: string
  /** Path relative to the project root. */
  path: string
  mode: Mode
  render(): string
}

const BEGIN = "<!-- clovejs:begin -->"
const END = "<!-- clovejs:end -->"

const GENERATED = "Generated by `npx clove skills`. Re-run it to update."

/** YAML front matter, hand-rolled to keep the CLI dependency-free. */
function frontMatter(fields: Record<string, string | string[] | boolean>): string {
  const lines = Object.entries(fields).map(([key, value]) => {
    if (Array.isArray(value)) return `${key}: ${value.join(",")}`
    if (typeof value === "boolean") return `${key}: ${value}`
    return `${key}: ${value.includes(":") ? JSON.stringify(value) : value}`
  })
  return `---\n${lines.join("\n")}\n---\n`
}

export const TARGETS: SkillTarget[] = [
  {
    id: "claude",
    label: "Claude Code",
    path: `.claude/skills/${SKILL_NAME}/SKILL.md`,
    mode: "own",
    render: () =>
      frontMatter({ name: SKILL_NAME, description: SKILL_DESCRIPTION }) +
      `\n# CloveJS\n\n${SKILL_BODY}\n`,
  },
  {
    id: "cursor",
    label: "Cursor",
    path: `.cursor/rules/${SKILL_NAME}.mdc`,
    mode: "own",
    render: () =>
      frontMatter({
        description: SKILL_DESCRIPTION,
        globs: SKILL_GLOBS,
        alwaysApply: false,
      }) + `\n# CloveJS\n\n${SKILL_BODY}\n`,
  },
  {
    id: "antigravity",
    label: "Antigravity",
    path: `.antigravity/rules/${SKILL_NAME}.md`,
    mode: "own",
    render: () =>
      frontMatter({ description: SKILL_DESCRIPTION, globs: SKILL_GLOBS }) +
      `\n# CloveJS\n\n${SKILL_BODY}\n`,
  },
  {
    // Codex reads AGENTS.md, as do Gemini CLI, Jules, Amp and others. It is a
    // project-owned file, so we contribute a block rather than the whole file.
    id: "codex",
    label: "Codex (AGENTS.md)",
    path: "AGENTS.md",
    mode: "share",
    render: () => `## CloveJS\n\n${SKILL_BODY}\n`,
  },
]

export interface InstallOptions {
  rootDir: string
  /** Target ids to write. Defaults to all of them. */
  ides?: string[]
  /** Overwrite files we own that already exist. */
  force?: boolean
}

export interface InstallResult {
  written: string[]
  updated: string[]
  skipped: string[]
}

/** Ids that are not known targets, for the caller to report. */
export function unknownIdes(ides: string[]): string[] {
  const known = new Set(TARGETS.map((t) => t.id))
  return ides.filter((id) => !known.has(id))
}

/**
 * Writes the CloveJS instructions in each editor's own format.
 *
 * Files we own are left alone unless `force` is set. Shared files (AGENTS.md)
 * are always reconciled in place: our block is replaced and everything else in
 * the file is preserved, which makes re-running the command idempotent.
 */
export async function installSkills(options: InstallOptions): Promise<InstallResult> {
  const { rootDir, force = false } = options
  const ids = options.ides?.length ? new Set(options.ides) : null
  const targets = ids ? TARGETS.filter((t) => ids.has(t.id)) : TARGETS

  const result: InstallResult = { written: [], updated: [], skipped: [] }

  for (const target of targets) {
    const full = join(rootDir, target.path)
    const existing = await readFile(full, "utf8").catch(() => null)

    if (target.mode === "own") {
      if (existing !== null && !force) {
        result.skipped.push(target.path)
        continue
      }
      await write(full, target.render())
      ;(existing === null ? result.written : result.updated).push(target.path)
      continue
    }

    const block = `${BEGIN}\n<!-- ${GENERATED} -->\n\n${target.render().trim()}\n${END}`
    if (existing === null) {
      await write(full, `# AGENTS.md\n\n${block}\n`)
      result.written.push(target.path)
      continue
    }
    const merged = mergeBlock(existing, block)
    if (merged === existing) {
      result.skipped.push(target.path)
      continue
    }
    await write(full, merged)
    result.updated.push(target.path)
  }

  return result
}

/** Replaces an existing clovejs block, or appends one if there is none. */
function mergeBlock(existing: string, block: string): string {
  const start = existing.indexOf(BEGIN)
  const end = existing.indexOf(END)

  if (start !== -1 && end > start) {
    const replaced =
      existing.slice(0, start) + block + existing.slice(end + END.length)
    return replaced === existing ? existing : replaced
  }

  return `${existing.replace(/\s*$/, "")}\n\n${block}\n`
}

async function write(full: string, contents: string): Promise<void> {
  await mkdir(join(full, ".."), { recursive: true })
  await writeFile(full, contents, "utf8")
}
